import { Injectable } from "@angular/core";

import { InvoiceHeader, Shipment, ShipmentStatusEnum } from "app/model/entities/entity-model";
import { ShippingBox } from "app/model/entities/shipping-box";
import { InvoiceStatusCode } from "app/model/enums/invoice-status-code";
import { WeightUnitOfMeasureCode } from "app/model/enums/unit-of-measure-code";
import { Address, AdvancedShipmentOptions, Dimensions, ModelPackage, Rate, RateShipmentResponseBody, Weight } from "app/model/shipEngine/models";
import { BusyService } from "app/shared/busy.service";
import { DbQueryService } from "app/shared/db-query.service";
import { DbSaveService } from "app/shared/db-save.service";
import { DialogBaseComponent } from "app/shared/dialog-base.component";
import { DialogWrapperService } from "app/shared/dialog-wrapper.service";
import { PrintFns } from "app/shared/print-fns";
import { UnitOfWork } from "app/shared/unit-of-work";
import { ShippingService } from "app/shipping/shipping.service";
import _ from "lodash";
import { DialogService } from "primeng/dynamicdialog";
import { AskShipInfoDialogComponent } from "./ask-ship-info-dialog.component";

interface RateResult {
  ok: boolean;
  freightAmt?: number;
  shipmentApiIdentifier?: string;
  error?: string;
}

interface RatesResult {
  ok: boolean;
  error?: string;
  shipmentApiIdentifier?: string;
  rates?: Rate[];
}

interface PrintLabelResult {
  ok: boolean;
  labelApiIdentifier?: string;
  shippingLabel?: string;
  error?: string;
}

@Injectable({ providedIn: "root" })
export class InvoiceShipmentService {
  constructor(
    private uow: UnitOfWork,
    private dbSaveService: DbSaveService,
    private dbQueryService: DbQueryService,
    private shippingService: ShippingService,
    private dialogWrapperService: DialogWrapperService,
    private busyService: BusyService,
    public pngDialogService: DialogService
  ) {}

  async askAndCalcFreight(invoiceHeader: InvoiceHeader) {
    // ensure that we have all needed entities loaded for calculation
    await this.dbQueryService.getInvoicesByJobOrderForFreightCalc(invoiceHeader.joHeaderId);

    const shipment = await this.ensureShipment(invoiceHeader);

    this.applyHandlingCharges(invoiceHeader);

    // Get all invoices that share THIS shipment (not all invoices on the JO)
    // This ensures the dialog is only shown once for this shipment
    const invoices: InvoiceHeader[] = shipment.invoiceHeaders.slice();

    // Show the dialog once for this shipment
    const r = await this.askAndCalcRate(invoiceHeader);
    if (r != null) {
      shipment.freightAmt = r.freightAmt;
      shipment.shipmentApiIdentifier = r.shipmentApiIdentifier;
    } else {
      this.dbSaveService.rejectChanges();
      return false;
    }

    this.recalcInvoiceTotals(invoices);

    await this.dbSaveService.saveChanges();
    return true;
  }

  /** Release the invoice's shipment for customer pickup instead of buying a carrier label.
   * No ShipEngine call, no freight, no label, no boxes — but the invoices still advance to
   * LABELLED so they land on End of Day exactly like a labeled carrier shipment would.
   *
   * The Shipment row is deliberately still created: the QB post path, the ShipmentStatus
   * lifecycle and the Proximity ShipmentDTO all hang off it. */
  async releaseForPickup(invoiceHeader: InvoiceHeader) {
    // Checked before anything is created or mutated, so there is nothing to unwind and no
    // need for a blanket rejectChanges() that would discard unrelated pending edits.
    if (invoiceHeader.hasShippingLabel) {
      await this.dialogWrapperService.showOkMessage(
        "Unable to release for pickup",
        "This shipment already has a carrier shipping label. Void the label first."
      );
      return false;
    }

    // ensure that we have all needed entities loaded for the totals calculation
    await this.dbQueryService.getInvoicesByJobOrderForFreightCalc(invoiceHeader.joHeaderId);

    const shipment = await this.ensureShipment(invoiceHeader);

    shipment.isPickup = true;
    // a pickup never had a carrier quote — clear anything a prior Calculate Freight left behind
    shipment.freightAmt = null;
    shipment.shipmentApiIdentifier = null;
    shipment.modTs = new Date(); // for OC purposes

    this.applyHandlingCharges(invoiceHeader);

    const invoices: InvoiceHeader[] = shipment.invoiceHeaders.slice();
    this.recalcInvoiceTotals(invoices);

    invoices.forEach((inv) => {
      inv.invoiceStatusId = InvoiceStatusCode.VoucherLabeled;
    });

    await this.dbSaveService.saveChanges();
    return true;
  }

  /** Return a pickup-released shipment to the voucher pool so it can be re-released or shipped
   * by carrier instead. The counterpart to voidShippingLabel, minus the carrier call — there is
   * no label to void, so nothing can fail and nothing lands in VOIDERR. */
  async undoPickupRelease(invoiceHeader: InvoiceHeader) {
    await this.dbQueryService.checkRowVersion(invoiceHeader);

    const shipment = invoiceHeader.shipment;
    shipment.invoiceHeaders.forEach((inv) => {
      inv.shipTs = null;
      inv.invoiceStatusId = InvoiceStatusCode.Voucher;
    });

    // clear the flag so the carrier path (Calculate Freight then Print Shipping Label) is available again
    shipment.isPickup = false;
    shipment.modTs = new Date(); // for OC purposes

    await this.dbSaveService.saveChanges();
    return true;
  }

  /** Resolve (or create) the Shipment for an invoice and wire up the navigation property. */
  private async ensureShipment(invoiceHeader: InvoiceHeader): Promise<Shipment> {
    let shipment: Shipment;
    // in this case the box doesn't exist yet
    if (invoiceHeader.shipmentId == null) {
      shipment = this.uow.createEntity(Shipment, {
        primaryInvoiceHeaderId: invoiceHeader.id,
        shipmentStatusId: ShipmentStatusEnum.New,
      });
      invoiceHeader.shipmentId = shipment.id;
    } else {
      if (invoiceHeader.shipment == null) {
        shipment = await this.dbQueryService.getShipment(invoiceHeader.shipmentId);
      } else {
        shipment = invoiceHeader.shipment;
      }
    }

    // IMPORTANT: Set the navigation property so the dialog can access it
    invoiceHeader.shipment = shipment;
    return shipment;
  }

  /** Assign-then-add, so re-running this on the same primary invoice is idempotent.
   * Only ever called with a shipment's primary invoice. */
  private applyHandlingCharges(invoiceHeader: InvoiceHeader) {
    if (invoiceHeader.isPrimaryInvoice) {
      // if multiple invoices for a JO, we put the handling on the same invoice that has the freight
      invoiceHeader.orderHandlingAmt = invoiceHeader.joHeader.handlingChargePerOrderAmt;
    }

    invoiceHeader.orderHandlingAmt += invoiceHeader.joHeader.handlingChargePerShipmentAmt;
  }

  /** Compute totals for every invoice sharing a shipment. Must run on both the carrier path and
   * the pickup path — an invoice that never gets here posts to QB with a stale totalAmt.
   *
   * calcTotalAmt() requires invoiceDetails to be loaded (for calcExtendedAmt). Cross-JO invoices
   * on the same shipment may not have details loaded — use persisted subtotalAmt as fallback. */
  private recalcInvoiceTotals(invoices: InvoiceHeader[]) {
    for (const invoice of invoices) {
      invoice.freightAmt = invoice.calcShipFreightAmt();
      const subtotal = invoice.calcExtendedAmt() ?? invoice.subtotalAmt ?? 0;
      const reissue = invoice.calcExtendedReissueAmt() ?? 0;
      invoice.totalAmt = subtotal + reissue + (invoice.taxAmt ?? 0) + (invoice.orderHandlingAmt ?? 0) + invoice.freightAmt - (invoice.prepaymentAmt ?? 0) - (invoice.pCardAmt ?? 0);
    }
  }

  async printShippingLabel(invoiceHeader: InvoiceHeader) {
    if (invoiceHeader.invoiceStatusId === InvoiceStatusCode.Voucher) {
      // Adding or removing an invoice clears the shipment's rate id but leaves the invoice freightAmt,
      // so the menu still offers this action. Without a rate id, ShipEngine rejects GetLabel/null.
      if (!invoiceHeader.shipment?.shipmentApiIdentifier) {
        await this.dialogWrapperService.showOkMessage(
          "Calculate the Freight",
          "This shipment does not have a current freight rate. Use 'Calculate Freight' to get a new rate. Then print the shipping label again."
        );
        return false;
      }
      // retrieve and save the label, and update status to labelled
      const r = await this.busyService.wrapBusy(async () => {
        await this.dbQueryService.checkRowVersion(invoiceHeader);
        // return await this.printShippingLabelCore(invoiceHeader.shipment);
        return await this.saveShippingLabels(invoiceHeader.shipment);
      });
      if (!r.ok) {
        return false;
      }
    }

    const r = await this.printShippingLabels(invoiceHeader.shipment);
    return r.ok;
  }

  async voidShippingLabel(invoiceHeader: InvoiceHeader) {
    // A pickup shipment has no carrier label. Without this the ShipEngine void would fail and
    // the catch-all below would strand every invoice on the shipment in VOIDERR.
    // undoPickupRelease is the correct counterpart for those.
    if (invoiceHeader.isPickup) {
      await this.dialogWrapperService.showOkMessage(
        "Unable to void label",
        "This shipment was released for customer pickup and has no shipping label. Use Undo Pickup Release instead."
      );
      return false;
    }

    const r = await this.busyService.wrapBusy(async () => {
      await this.dbQueryService.checkRowVersion(invoiceHeader);
      return await this.voidShippingLabelCore(invoiceHeader.shipment);
    });

    const shipment = invoiceHeader.shipment;
    if (r.ok) {
      // TODO: write to VoidShipment table here
      shipment.invoiceHeaders.forEach((inv) => {
        inv.shipTs = null;
        inv.invoiceStatusId = InvoiceStatusCode.Voucher;
      });

      shipment.shippingBoxes.forEach((sb) => {
        sb.trackingLabelApiIdentifier = null;
        sb.shippingLabel = null;
      });
    } else {
      shipment.invoiceHeaders.forEach((inv) => {
        inv.invoiceStatusId = InvoiceStatusCode.VoidError;
      });
    }

    await this.dbSaveService.saveChanges();
    return r.ok;
  }

  async removeVoidError(invoiceHeader: InvoiceHeader) {
    await this.dbQueryService.checkRowVersion(invoiceHeader);

    const shipment = invoiceHeader.shipment;
    const invoices = shipment.invoiceHeaders;
    if (!invoices.every((inv) => inv.invoiceStatusId === InvoiceStatusCode.VoidError)) {
      await this.dialogWrapperService.showOkMessage(
        "Unable to remove void error",
        'Unable to remove the "void-error" on this shipment because all of invoices in it are not marked with a "void error"'
      );
      return false;
    }

    invoices.forEach((inv) => {
      inv.invoiceStatusId = InvoiceStatusCode.Voucher;
    });

    shipment.freightAmt = null;
    shipment.shipmentApiIdentifier = null;
    shipment.labelApiIdentifier = null;

    shipment.shippingBoxes.forEach((sb) => {
      sb.trackingLabelApiIdentifier = null;
      sb.shippingLabel = null;
    });

    await this.dbSaveService.saveChanges();
    return true;
  }

  // returns true if new rate was calc'd
  private async askAndCalcRate(invoiceHeader: InvoiceHeader) {
    const shipment = invoiceHeader.shipment;
    let isShipInfoNeeded = true;
    // loop thru dialog until either canceled or we get a good rate.
    while (isShipInfoNeeded) {
      const shouldContinue = await DialogBaseComponent.open(AskShipInfoDialogComponent, this, { shipment: shipment });
      if (!shouldContinue) {
        return null;
      }

      const r = await this.busyService.wrapBusy(async () => {
        return await this.getRate(invoiceHeader);
      });
      if (r.ok) {
        isShipInfoNeeded = false;
        return r;
      }
    }
    // return true;
  }

  /** Get all rates for the given invoice shipment from the given carrier. */
  async getRatesForInvoice(
    invoiceHeader: InvoiceHeader,
    carrierId: string,
    serviceId: string,
    showErrorDialog = true
  ): Promise<RatesResult> {
    const shipTo = this.createAddress(invoiceHeader);
    const options = this.createAdvancedOptions(invoiceHeader);
    const shipment = invoiceHeader.shipment;
    const dimensionError = this.getBoxDimensionError(shipment.shippingBoxes);
    if (dimensionError) {
      return await this.handleError("Box Dimensions Are Not Complete", dimensionError, showErrorDialog);
    }
    const modelPackages = this.createPackages(shipment.shippingBoxes);

    let rateShipmentResponse: RateShipmentResponseBody;
    await this.busyService.wrapBusy(async () => {
      rateShipmentResponse = await this.shippingService.getRates(shipTo, modelPackages, carrierId, serviceId, options);
    });

    // Note 'errors' property will be there in some circumstances ( mixed weight types for example)
    // but it is not defined via the api
    const responseErrors = rateShipmentResponse["errors"];
    if (responseErrors && responseErrors.length > 0) {
      const error = responseErrors.map((e) => e.message).join("/n");
      return await this.handleError("Shipping Error", error, showErrorDialog);
    }

    const rateResponse = rateShipmentResponse.rate_response;
    const rateErrors = rateResponse.errors;
    if (rateErrors && rateErrors.length > 0) {
      const error = rateErrors.map((e) => e.message).join("/n");
      return await this.handleError("Shipping Error", error, showErrorDialog);
    }

    return { ok: true, shipmentApiIdentifier: rateShipmentResponse.shipment_id, rates: rateResponse.rates };
  }

  /** Get a single rate matching the shipment.serviceApiIdentifier (e.g. 'ups_ground') */
  private async getRate(invoiceHeader: InvoiceHeader, showErrorDialog = true): Promise<RateResult> {
    const shipment = invoiceHeader.shipment;
    const ratesResponse = await this.getRatesForInvoice(
      invoiceHeader,
      shipment.carrierApiIdentifier,
      shipment.serviceApiIdentifier,
      showErrorDialog
    );

    const shipmentApiIdentifier = ratesResponse.shipmentApiIdentifier;

    const rates = ratesResponse.rates;
    const rate = _.find(rates, (r) => r.service_code === shipment.serviceApiIdentifier);
    if (rate == null) {
      const error = "Unable to locate a rate for " + shipment.serviceApiIdentifier;
      return await this.handleError("Shipping Error", error, showErrorDialog);
    }

    // NOTE: currency is assumed to be $
    const freightAmt =
      rate.shipping_amount.amount + rate.insurance_amount.amount + rate.confirmation_amount.amount + rate.other_amount.amount;

    return { ok: true, freightAmt, shipmentApiIdentifier };
  }

  /** Get shipping label from ShipEngine and update shipment and shippingBoxes */
  private async saveShippingLabels(shipment: Shipment, showErrorDialog = true): Promise<PrintLabelResult> {
    const labelResponse = await this.shippingService.getLabel(shipment.shipmentApiIdentifier);

    const labelErrors = labelResponse["errors"];
    let error: string;
    if (labelErrors) {
      error = labelErrors.map((e) => e.message).join("; ");
      return await this.handleError("Shipping Label Error", error, showErrorDialog);
    }

    const labelDownload = labelResponse.label_download.href;
    const labelPackages = labelResponse.packages;
    const labelFormat = labelResponse.label_format;
    const boxes = shipment.shippingBoxes;

    // update box corresponding to each label package
    labelPackages.forEach((lp) => {
      const box = boxes.find((b) => b.externalPackageId === lp.external_package_id);
      if (box) {
        box.trackingLabelApiIdentifier = lp.tracking_number?.trim();
        box.shippingLabel = (lp.label_download && lp.label_download[labelFormat]) || labelDownload;
      }
    });

    // shipment.label = labelResponse.label_download && labelResponse.label_download.href;
    shipment.labelApiIdentifier = labelResponse.label_id;
    shipment.trackingUrl = labelResponse.tracking_url;
    shipment.invoiceHeaders.forEach((inv) => {
      inv.invoiceStatusId = InvoiceStatusCode.VoucherLabeled;
    });

    await this.dbSaveService.saveChanges();
    return { ok: true, labelApiIdentifier: shipment.labelApiIdentifier };
  }

  /** Print the labels in the shippingBoxes belonging to the shipment.
   * Each box.shippingLabel is a url for downloading the label.
   */
  private async printShippingLabels(shipment: Shipment) {
    const boxes = shipment.shippingBoxes;
    if (!boxes.length || !boxes.every((box) => box.shippingLabel)) {
      return await this.handleError("Label Printing Error", "No boxes, or boxes do not have shipping labels", true);
    }
    return this.printBoxShippingLabels(boxes);
  }

  async printBoxShippingLabels(boxes: ShippingBox[]) {
    // download the label content from the shippingLabel urls on the boxes
    const prints = boxes.map((box) => ({ url: box.shippingLabel, name: box.externalPackageId, data: null }));

    // download data if QZ is not available to do it when printing
    const hasQZ = await PrintFns.startQZ();
    if (!hasQZ) {
      await this.shippingService.downloadFileBlobs(prints);
    }

    await PrintFns.printFiles(prints, "labelPrinter");

    return { ok: true };
  }

  private async voidShippingLabelCore(shipment: Shipment, showErrorDialog = true) {
    // Do NOT do next line because we want to make sure than we get an opto-conc error if shipment is changed by another user.
    // const shipment = await this.dbQueryService.getShipment(shipmentId);

    // Steve: Review this - probably not what is needed to void a label.
    const labelApiIdentifier = shipment.labelApiIdentifier;
    if (labelApiIdentifier == null) {
      return { ok: false };
    }

    const voidResponse = await this.shippingService.voidLabel(labelApiIdentifier);
    // errors is not defined on the VoidResponse def ... but it exists.
    let error: string = null;
    if (voidResponse["errors"]) {
      error = voidResponse["errors"].map((e) => e.message).join("/n");
    } else if (!voidResponse.approved) {
      error = voidResponse.message;
    }

    if (error) {
      return await this.handleError("Shipping Label Void Error", error, showErrorDialog);
    } else {
      shipment.labelApiIdentifier = null;
      return { ok: true };
    }
  }

  private async handleError(title: string, error: string, showErrorDialog: boolean) {
    if (showErrorDialog) {
      await this.dialogWrapperService.showOkMessage(title, error);
    }
    return { ok: false, error: title + ": " + error };
  }

  private createAddress(invoiceHeader: InvoiceHeader) {
    const joh = invoiceHeader.joHeader;
    const address = <Address>{
      name: joh.shippingName || "",
      address_line1: joh.shippingAddress1 || "",
      address_line2: joh.shippingAddress2,
      address_line3: joh.shippingAddress3,
      city_locality: joh.shippingCity || "",
      state_province: joh.shippingState || "",
      postal_code: (joh.shippingZipCode || "").trim().replace(/-$/, ""), // trims trailing '-'
      country_code: "US",
      phone: joh.shippingTelephone || joh.billingTelephone,
    };
    return address;
  }

  /** Create ShipEngine advanced options for third-party billing.  Returns null if no options applicable. */
  private createAdvancedOptions(invoiceHeader: InvoiceHeader) {
    const joh = invoiceHeader.joHeader;
    if (!joh.carrierAccountNumber) {
      return null;
    }

    const opts = <AdvancedShipmentOptions>{
      bill_to_account: joh.carrierAccountNumber,
      bill_to_postal_code: joh.carrierAccountPostalCode,
      bill_to_country_code: "US",
      bill_to_party: "third_party",
    };
    return opts;
  }

  /** A box may have no dimensions at all, but if it has any, ShipEngine requires all three to be > 0.
   * createPackages would otherwise send a missing value as 0. Rows match the Box Weights grid order. */
  private getBoxDimensionError(shippingBoxes: ShippingBox[]): string | null {
    for (let i = 0; i < shippingBoxes.length; i++) {
      const sb = shippingBoxes[i];
      const dims = { length: sb.length, width: sb.width, height: sb.height };
      const hasAny = Object.values(dims).some((v) => v != null && (v as any) !== "");
      if (!hasAny) {
        continue;
      }
      const bad = Object.keys(dims).filter((k) => !(+dims[k] > 0));
      if (bad.length === 0) {
        continue;
      }
      const names = bad.length === 1 ? bad[0] : bad.length === 2 ? bad.join(" and ") : "length, width, and height";
      return (
        `The ${names} of the box in row ${i + 1} must be more than 0. ` +
        `Enter the correct ${names}. You can also select a box size that has dimensions.`
      );
    }
    return null;
  }

  private createPackages(shippingBoxes: ShippingBox[]) {
    // TODO: create a mapping lookup for this
    let unit: string;
    const packages = shippingBoxes.map((sb) => {
      const uomId = sb.weightUoMId;
      if (uomId === WeightUnitOfMeasureCode.Ounce) {
        unit = Weight.UnitEnum.Ounce;
      } else if (uomId === WeightUnitOfMeasureCode.Pound) {
        unit = Weight.UnitEnum.Pound;
      } else if (uomId === WeightUnitOfMeasureCode.Gram) {
        unit = Weight.UnitEnum.Gram;
      } else if (uomId === WeightUnitOfMeasureCode.Kilogram) {
        unit = Weight.UnitEnum.Kilogram;
      }
      const modelPackage = <ModelPackage>{
        weight: {
          unit: unit,
          value: sb.weight,
        },
      };
      if (sb.packageApiIdentifier != null) {
        modelPackage.package_code = sb.packageApiIdentifier;
      }
      if (sb.length != null || sb.width != null || sb.height != null) {
        modelPackage.dimensions = {
          unit: Dimensions.UnitEnum.Inch,
          length: sb.length || 0,
          width: sb.width || 0,
          height: sb.height || 0,
        };
      }
      modelPackage.external_package_id = sb.externalPackageId;
      return modelPackage;
    });
    return packages;
  }

  /** download and print shipping labels.  print 1 label for each url. */
  // Not currently used
  // printShippingLabelsForUrls(urls: string[]) {
  //   const fetched = {};
  //   urls.forEach(url => {
  //     url += '?download=0';
  //     if (!fetched[url]) {
  //       this.http.get(url).toPromise().then(r => {
  //         const zpl = r as string;
  //         fetched[url] = zpl;
  //         PrintFns.printZpl(zpl, 'labelPrinter');
  //       });
  //     } else {
  //       PrintFns.printZpl(fetched[url], 'labelPrinter');
  //     }
  //   });
  // }
}
